import type { PortalMoviePayload } from '../portal/portalDraftEnvelope';

/** Common age / certification labels for the movie editor (single-select). */
export const MOVIE_AGE_RATINGS = [
  'G',
  'PG',
  'PG-13',
  'R',
  'NC-17',
  'NR',
  'TV-MA',
  'U',
  '12A',
  '15',
  '18',
] as const;

export type MovieRatingSite = 'imdb' | 'rotten_tomatoes';

export function inferRatingSite(ratingSource?: string): MovieRatingSite {
  const s = (ratingSource || '').toLowerCase();
  if (s.includes('rotten') || s.includes('tomato')) return 'rotten_tomatoes';
  return 'imdb';
}

export function imdbSearchUrlForTitle(title: string): string {
  const q = title.trim();
  return `https://www.imdb.com/find/?q=${encodeURIComponent(q)}&s=tt`;
}

export function rottenTomatoesSearchUrlForTitle(title: string): string {
  const q = title.trim();
  return `https://www.rottentomatoes.com/search?search=${encodeURIComponent(q)}`;
}

export function defaultReviewSearchUrl(site: MovieRatingSite, title: string): string {
  return site === 'rotten_tomatoes' ? rottenTomatoesSearchUrlForTitle(title) : imdbSearchUrlForTitle(title);
}

/** Tab logos (SVG). */
export const MOVIE_TAB_LOGO = {
  imdb: 'https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg',
  rottenTomatoes:
    'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Rotten_Tomatoes_logo.svg/1280px-Rotten_Tomatoes_logo.svg.png',
} as const;

export type MovieAvailabilityValue = 'watch_free' | 'rent' | 'not_available';

export const MOVIE_AVAILABILITY_OPTIONS: ReadonlyArray<{ value: MovieAvailabilityValue; label: string }> = [
  { value: 'watch_free', label: 'Watch for free' },
  { value: 'rent', label: 'Rent the movie' },
  { value: 'not_available', label: 'Not available' },
];

export function normalizeMovieAvailability(raw: string | undefined): MovieAvailabilityValue {
  const v = (raw || '').trim().toLowerCase();
  if (v === 'watch_free' || v === 'watch for free') return 'watch_free';
  if (v === 'rent' || v === 'rent_the_movie' || v === 'rent the movie') return 'rent';
  if (v === 'not_available' || v === 'not available' || v === 'unknown' || v === '') return 'not_available';
  return 'not_available';
}

/** Value for the review URL text field (split fields + legacy fallback). */
export function reviewUrlInputValue(movie: PortalMoviePayload, site: MovieRatingSite): string {
  if (site === 'imdb') {
    if (movie.rating_review_url_imdb != null && movie.rating_review_url_imdb !== '') return movie.rating_review_url_imdb;
    if (inferRatingSite(movie.rating_source) === 'imdb' && movie.rating_review_url) return movie.rating_review_url;
    return '';
  }
  if (movie.rating_review_url_rotten_tomatoes != null && movie.rating_review_url_rotten_tomatoes !== '')
    return movie.rating_review_url_rotten_tomatoes;
  if (inferRatingSite(movie.rating_source) === 'rotten_tomatoes' && movie.rating_review_url) return movie.rating_review_url;
  return '';
}

export function saveReviewUrlForSite(
  movie: PortalMoviePayload,
  site: MovieRatingSite,
  url: string
): PortalMoviePayload {
  const src = inferRatingSite(movie.rating_source);
  if (site === 'imdb') {
    return {
      ...movie,
      rating_review_url_imdb: url,
      ...(src === 'imdb' ? { rating_review_url: url } : {}),
    };
  }
  return {
    ...movie,
    rating_review_url_rotten_tomatoes: url,
    ...(src === 'rotten_tomatoes' ? { rating_review_url: url } : {}),
  };
}

/** Resolved URL for preview (trimmed split or legacy, or empty). */
export function effectiveReviewUrlForSite(movie: PortalMoviePayload, site: MovieRatingSite): string {
  return reviewUrlInputValue(movie, site).trim();
}

/** 1–5 stars for a source tab; falls back to legacy `rating` when source matches and split unset. */
export function tabSourceStarRating(movie: PortalMoviePayload, site: MovieRatingSite): number | null {
  if (site === 'imdb') {
    if (movie.rating_imdb != null && movie.rating_imdb > 0) return movie.rating_imdb;
    if (movie.rating_imdb == null && inferRatingSite(movie.rating_source) === 'imdb' && movie.rating > 0) return movie.rating;
    return null;
  }
  if (movie.rating_rotten_tomatoes != null && movie.rating_rotten_tomatoes > 0) return movie.rating_rotten_tomatoes;
  if (movie.rating_rotten_tomatoes == null && inferRatingSite(movie.rating_source) === 'rotten_tomatoes' && movie.rating > 0)
    return movie.rating;
  return null;
}

export function saveSourceStarRating(movie: PortalMoviePayload, site: MovieRatingSite, stars: number): PortalMoviePayload {
  if (site === 'imdb') {
    return { ...movie, rating_imdb: stars, rating_source: 'imdb' };
  }
  return { ...movie, rating_rotten_tomatoes: stars, rating_source: 'rotten_tomatoes' };
}

export function autoGenerateDiscussionQuestions(movieTitle: string, plotSummary: string): string[] {
  const t = movieTitle.trim() || 'this film';
  const p = plotSummary.trim();
  const out = [
    `What central conflict drives “${t}” and how is it resolved?`,
    `Which performance or scene felt most essential to the story?`,
    `What tone or genre conventions does “${t}” embrace or subvert?`,
    `If you recommended “${t}” to a friend, what would you highlight without spoilers?`,
  ];
  if (p.length > 80) {
    out.push(`How does the setup in the first act prepare the audience for the rest of “${t}”?`);
  }
  return out;
}
